// Claims Desk — the worklist (§7's `GET /`, wireframe S2) plus the claim
// workspace (§7's `GET /:id`, wireframe S3 — see ClaimWorkspace.tsx).
// Insurance Claims Phase 4 + 4.5.2. KPI tiles, "needs action" default sort,
// Status/TAT/Owner/Mode/Payer filters and search are all computed
// client-side from the one `GET /claims` list response (which Phase 4.5.2
// extended with `running_bill`/`enhancement` per eligible row — see
// routes/claims.js's own comment on why that's safe to compute inline).
import { useMemo, useState } from 'react';
import { ApiError } from '@/lib/api/client';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { KpiTile } from '@/components/ui/KpiTile';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { useAuth } from '@/hooks/useAuth';
import { useClaims, useUsersMap } from './hooks';
import { ClaimWorkspacePanel } from './ClaimWorkspace';
import { ReceivablesReports } from './ReceivablesReports';
import { useHideTopbar } from '@/lib/topbarFocus';
import { sortByNeedsAction, nextActionFor } from './worklistUtils';
import type { Claim, ClaimStatus } from './types';

// Terminal/post-discharge statuses (mirrors backend/routes/claims.js's own
// TERMINAL_OR_POST_DISCHARGE_STATUSES) — once a claim is here, the Ward
// column reads "discharged" (wireframe S2's own J. Mary row) rather than the
// bed the patient no longer occupies.
const POST_DISCHARGE_STATUSES: ClaimStatus[] = [
  'discharged', 'submitted', 'settled', 'short_settled', 'rejected', 'appealed', 'closed',
];

// Wireframe S2's quick-filter pill row (Needs action / All open / Submitted /
// Settled) — a coarse, one-click status group, not the full 19-value
// dropdown. The dropdown stays alongside for a specific status a pill can't
// express (e.g. "preauth_query" alone).
type QuickFilter = 'needs_action' | 'all_open' | 'submitted' | 'settled';
const OPEN_STATUSES: ClaimStatus[] = [
  'draft', 'preauth_sent', 'preauth_query', 'preauth_approved', 'enhancement_sent',
  'final_auth_sent', 'final_auth_query', 'final_approved', 'discharged',
];

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', preauth_sent: 'Pre-auth sent', preauth_query: 'Pre-auth query',
  preauth_approved: 'Pre-auth approved', preauth_denied: 'Pre-auth denied',
  enhancement_sent: 'Enhancement sent', final_auth_sent: 'Final auth sent',
  final_auth_query: 'Final auth query', final_approved: 'Final approved',
  discharged: 'Discharged', submitted: 'Submitted', claim_query: 'Claim query',
  settled: 'Settled', short_settled: 'Short settled', rejected: 'Rejected',
  appealed: 'Appealed', closed: 'Closed', cancelled: 'Cancelled', documents_issued: 'Documents issued',
};
const MODE_LABEL: Record<string, string> = { cashless: 'Cashless', reimbursement: 'Reimbursement', scheme: 'Scheme', corporate: 'Corporate' };
const QUERY_STATUSES: ClaimStatus[] = ['preauth_query', 'final_auth_query', 'claim_query'];
const AWAITING_PREAUTH: ClaimStatus[] = ['preauth_sent', 'preauth_query'];
const AWAITING_FINAL: ClaimStatus[] = ['final_auth_sent', 'final_auth_query'];

function money(v: number | string | null | undefined): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n == null || Number.isNaN(n) ? '—' : `₹${n.toFixed(2)}`;
}

/** "70,000" — whole rupees, Indian grouping, no ₹/paise (wireframe S2); "—" for none. */
function wholeRupees(v: number | string | null | undefined): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (n == null || Number.isNaN(n) || n === 0) return '—';
  return Math.round(n).toLocaleString('en-IN');
}

/** "IP/2026-2027/000412" → "IP/…/000412" (wireframe S2); the full number is the cell's title. */
function shortIpNo(ip: string): string {
  const parts = ip.split('/');
  return parts.length > 2 ? `${parts[0]}/…/${parts[parts.length - 1]}` : ip;
}

function firstName(name: string | undefined): string | undefined {
  if (!name) return undefined;
  const words = name.trim().split(/\s+/);
  return words.length > 1 && /^(dr\.?|mr\.?|ms\.?|mrs\.?)$/i.test(words[0] ?? '') ? words[1] : words[0];
}

function minutesText(m: number): string {
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${m} m`;
}

/** Wireframe S2 TAT cell: an open authorisation's elapsed time as coloured
 * timer text (red past target, amber nearing it); otherwise a pill — query
 * reply due, enhancement % of approved, or submission ageing in days. */
function TatCell({ claim: c }: { claim: Claim }) {
  const a = c.tat?.authorization;
  if (a && !a.decided) {
    const cls = a.breached ? 'text-danger-ink' : a.amber ? 'text-warn-ink' : 'text-ink';
    return <span className={`font-bold tabular-nums ${cls}`}>{minutesText(a.elapsed_min)}</span>;
  }
  const q = c.tat?.query;
  if (q && !q.replied) {
    const due = new Date(q.due_at);
    const sameDay = due.toDateString() === new Date().toDateString();
    const when = sameDay
      ? due.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : due.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    return <Badge variant={q.breached ? 'danger' : 'warn'}>Query due {when}</Badge>;
  }
  if (c.enhancement?.due && c.enhancement.ratio != null) {
    return <Badge variant="warn">{Math.round(c.enhancement.ratio * 100)} %</Badge>;
  }
  const s = c.tat?.submission_ageing;
  if (s && c.status === 'discharged') {
    return <Badge variant={s.breached ? 'danger' : 'neutral'}>{s.elapsed_days} day{s.elapsed_days === 1 ? '' : 's'}</Badge>;
  }
  return <span className="text-ink-muted">—</span>;
}

/** Wireframe S2 status pills: amber while the payer has it (sent / query),
 * green once approved or settled, red when denied, plain otherwise. */
function StatusPill({ status }: { status: ClaimStatus }) {
  const variant: 'warn' | 'success' | 'danger' | 'neutral' =
    /_sent$|query|appealed|short_settled/.test(status) ? 'warn'
      : /approved$|^settled$|^closed$/.test(status) ? 'success'
        : /denied|rejected|cancelled/.test(status) ? 'danger'
          : 'neutral';
  return <Badge variant={variant}>{STATUS_LABEL[status] ?? status}</Badge>;
}

export function ClaimsModule() {
  const { user } = useAuth();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('needs_action');
  const [statusFilter, setStatusFilter] = useState('');
  const [tatOnly, setTatOnly] = useState(false);
  const [modeFilter, setModeFilter] = useState('');
  const [payerFilter, setPayerFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<'' | 'me'>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // An open claim has its own sticky ClaimHeader — hide App's top bar meanwhile.
  useHideTopbar(selectedId != null);
  // Desk (worklist, S2) or Receivables & reports (S8, Phase 7).
  const [view, setView] = useState<'desk' | 'reports'>('desk');
  const usersMap = useUsersMap();

  const list = useClaims({ status: statusFilter || undefined, tat: tatOnly ? 'breached' : undefined });
  const allClaims = useMemo(() => list.data ?? [], [list.data]);

  const payerOptions = useMemo(
    () => [...new Set(allClaims.map((c) => c.payer_name).filter((p): p is string => !!p))].sort(),
    [allClaims]
  );

  const kpis = useMemo(() => {
    const open = allClaims.filter((c) => !['closed', 'cancelled', 'settled', 'rejected'].includes(c.status));
    const preauthAwaiting = allClaims.filter((c) => AWAITING_PREAUTH.includes(c.status));
    const finalAwaiting = allClaims.filter((c) => AWAITING_FINAL.includes(c.status));
    const openQueries = allClaims.filter((c) => QUERY_STATUSES.includes(c.status));
    const enhancementDue = allClaims.filter((c) => c.enhancement?.due);
    const dischargedNotSubmitted = allClaims.filter((c) => c.status === 'discharged');
    const receivable = open.reduce((s, c) => {
      const approved = parseFloat(c.final_approved ?? c.approved_total ?? '0') || 0;
      const settled = parseFloat(c.settled_total ?? '0') || 0;
      return s + Math.max(0, approved - settled);
    }, 0);
    return { preauthAwaiting, finalAwaiting, openQueries, enhancementDue, dischargedNotSubmitted, receivable, openCount: open.length };
  }, [allClaims]);

  const filtered = useMemo(() => {
    let rows = allClaims;
    if (quickFilter === 'needs_action') rows = sortByNeedsAction(rows).filter((c) => OPEN_STATUSES.includes(c.status));
    else if (quickFilter === 'all_open') rows = rows.filter((c) => OPEN_STATUSES.includes(c.status));
    else if (quickFilter === 'submitted') rows = rows.filter((c) => ['submitted', 'claim_query', 'short_settled'].includes(c.status));
    else if (quickFilter === 'settled') rows = rows.filter((c) => ['settled', 'closed'].includes(c.status));
    if (statusFilter) rows = rows.filter((c) => c.status === statusFilter);
    if (modeFilter) rows = rows.filter((c) => c.mode === modeFilter);
    if (payerFilter) rows = rows.filter((c) => c.payer_name === payerFilter);
    if (ownerFilter === 'me' && user?.id) rows = rows.filter((c) => c.owner_user_id === user.id);
    if (tatOnly) rows = rows.filter((c) => c.tat_breached);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((c) =>
        c.patient_name?.toLowerCase().includes(q) || c.uhid?.toLowerCase().includes(q) ||
        c.ip_number?.toLowerCase().includes(q) || c.claim_ref.toLowerCase().includes(q)
      );
    }
    return quickFilter === 'needs_action' ? rows : sortByNeedsAction(rows);
  }, [allClaims, quickFilter, statusFilter, modeFilter, payerFilter, ownerFilter, tatOnly, search, user?.id]);

  // Wireframe S2's worklist columns, cell for cell: TAT as a coloured timer
  // (red past target, amber nearing it) or an amber/red pill (query due,
  // enhancement %, submission ageing); bold patient + an abbreviated IP no.;
  // Approved / Running bill as plain whole rupees (no ₹, no paise), "—" when
  // there is none; status pills amber while with the payer, green once
  // approved/settled.
  const columns: DataTableColumn<Claim>[] = [
    { key: 'tat', header: 'TAT', widthClassName: 'w-[104px]', render: (c) => <TatCell claim={c} /> },
    {
      key: 'patient', header: 'Patient · IP No', sortable: true, sortValue: (c) => c.patient_name ?? '',
      render: (c) => (
        <span title={c.ip_number ?? undefined}>
          <b>{c.patient_name}</b>{c.ip_number && <> · {shortIpNo(c.ip_number)}</>}
        </span>
      ),
    },
    {
      key: 'ward', header: 'Ward',
      render: (c) => POST_DISCHARGE_STATUSES.includes(c.status)
        ? 'discharged'
        : c.ward_name ? `${c.ward_name}${c.bed_number ? ` ${c.bed_number}` : ''}` : '—',
    },
    { key: 'payer', header: 'Payer · TPA', render: (c) => c.payer_name },
    { key: 'mode', header: 'Mode', render: (c) => MODE_LABEL[c.mode] ?? c.mode },
    { key: 'status', header: 'Status', render: (c) => <StatusPill status={c.status} /> },
    {
      key: 'approved', header: 'Approved', align: 'right', sortable: true,
      sortValue: (c) => parseFloat(c.final_approved ?? c.approved_total ?? '0') || 0,
      render: (c) => wholeRupees(c.final_approved ?? c.approved_total),
    },
    { key: 'running', header: 'Running bill', align: 'right', render: (c) => (c.running_bill ? wholeRupees(c.running_bill.net) : '—') },
    { key: 'next', header: 'Next action', render: (c) => nextActionFor(c) },
    { key: 'owner', header: 'Owner', render: (c) => (c.owner_user_id != null ? firstName(usersMap.get(c.owner_user_id)) : undefined) ?? '—' },
  ];

  // Opening a claim replaces the worklist with a full-width workspace,
  // rather than appending it below the KPI tiles + table (the old layout —
  // the opened claim wasn't in view without scrolling past both, and the
  // sticky ClaimHeader inside it then detached visually from the rest of
  // the panel mid-scroll). Mirrors IPModule.tsx's own board ↔ admission-
  // detail toggle exactly (see its own `showBoard`/`detailPanel` split) —
  // one or the other renders, never both at once. "Back to Claims desk" and
  // "Close" both live inside ClaimHeader's own top row (same row as the
  // patient identity line) rather than as a separate element above the
  // panel. No wrapping `p-4` here, deliberately — ClaimHeader's sticky bleed
  // (`-mt-5 -mx-6`) is calibrated to cancel exactly ONE layer of padding,
  // App.tsx's own page-content wrapper; a second padded ancestor between it
  // and that wrapper clamps the bleed short (confirmed live: sticky's offset
  // is bounded by its nearest block ancestor's own content-box, not just the
  // scrolling container several levels up), leaving the old gap-above-the-
  // header bug in place. The worklist view below still gets its own `p-4`.
  if (selectedId != null) {
    return <ClaimWorkspacePanel claimId={selectedId} onClose={() => setSelectedId(null)} onBack={() => setSelectedId(null)} />;
  }

  const viewSwitch = (
    <div className="flex items-center gap-1.5">
      <Button size="sm" variant={view === 'desk' ? 'primary' : 'ghost'} onClick={() => setView('desk')}>Desk</Button>
      <Button size="sm" variant={view === 'reports' ? 'primary' : 'ghost'} onClick={() => setView('reports')}>Receivables &amp; reports</Button>
    </div>
  );

  if (view === 'reports') {
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-lg font-semibold">Claims receivables &amp; reports</h1>
          {viewSwitch}
        </div>
        <ReceivablesReports />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Claims desk</h1>
        {viewSwitch}
      </div>

      {/* Capped at 3-across (never 6-in-a-row) — a laptop's content width,
          net of the sidebar, rarely has six columns' worth of room at a
          readable font size; wrapping to two rows of three keeps each tile
          wide enough for "Final auth awaiting" to stay on one or two lines
          instead of clipping (the label no longer truncates at `size="sm"`
          either — see KpiTile.tsx). */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <KpiTile size="sm" icon="⏳" label="Pre-auth awaiting" value={kpis.preauthAwaiting.length}
          sub={kpis.preauthAwaiting.some((c) => c.tat_breached) ? `${kpis.preauthAwaiting.filter((c) => c.tat_breached).length} breached` : undefined} />
        <KpiTile size="sm" icon="📨" label="Final auth awaiting" value={kpis.finalAwaiting.length}
          sub={kpis.finalAwaiting.some((c) => c.tat?.authorization?.amber) ? 'some nearing target' : undefined} />
        <KpiTile size="sm" icon="❓" label="Open queries" value={kpis.openQueries.length} />
        <KpiTile size="sm" icon="📈" label="Enhancement due" value={kpis.enhancementDue.length} />
        <KpiTile size="sm" icon="🏁" label="Discharged, not submitted" value={kpis.dischargedNotSubmitted.length}
          sub={kpis.dischargedNotSubmitted.length > 0 ? 'build & submit the packet' : undefined} />
        <KpiTile size="sm" icon="💰" label="Receivable" value={money(kpis.receivable)} sub={`${kpis.openCount} open claims`} />
      </div>

      <Section title="Worklist">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant={quickFilter === 'needs_action' ? 'primary' : 'ghost'} onClick={() => setQuickFilter('needs_action')}>Needs action</Button>
          <Button size="sm" variant={quickFilter === 'all_open' ? 'primary' : 'ghost'} onClick={() => setQuickFilter('all_open')}>All open</Button>
          <Button size="sm" variant={quickFilter === 'submitted' ? 'primary' : 'ghost'} onClick={() => setQuickFilter('submitted')}>Submitted</Button>
          <Button size="sm" variant={quickFilter === 'settled' ? 'primary' : 'ghost'} onClick={() => setQuickFilter('settled')}>Settled</Button>
          <NativeSelect value={payerFilter} onChange={(e) => setPayerFilter(e.target.value)} className="w-40">
            <option value="">Payer ▾</option>
            {payerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </NativeSelect>
          <Button size="sm" variant={ownerFilter === 'me' ? 'primary' : 'ghost'} onClick={() => setOwnerFilter((v) => v === 'me' ? '' : 'me')}>
            Owner: me
          </Button>
          <NativeSelect value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="w-28">
            <option value="">Mode ▾</option>
            {Object.entries(MODE_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </NativeSelect>
          <Button size="sm" variant={tatOnly ? 'primary' : 'ghost'} onClick={() => setTatOnly((v) => !v)}>
            ⏱ TAT breached
          </Button>
          <NativeSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-40">
            <option value="">More statuses…</option>
            {Object.entries(STATUS_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </NativeSelect>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search name / UHID / IP / claim no."
            className="ml-auto w-64" />
        </div>
        <div className="mt-3">
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(c) => c.id}
            onRowClick={(c) => setSelectedId(c.id)}
            appearance="sheet"
            // Wireframe S2 `tr.hl`: a breached row is tinted, not striped.
            rowClassName={(c) => c.tat_breached ? 'bg-danger-soft/50' : undefined}
            isLoading={list.isLoading}
            isError={list.isError}
            errorMessage={list.error instanceof ApiError ? list.error.message : 'Could not load claims'}
            onRetry={() => list.refetch()}
            emptyLabel="No claims"
            emptyHint="No claim matches these filters."
          />
        </div>
      </Section>
    </div>
  );
}
